import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, LanguageSwitch } from '../components/shared';
import { api } from '../lib/api';
import { getApiErrorMessage } from '../lib/errors';

/**
 * `/forgot-password` — accepts an email address and asks the backend to
 * send a password-reset email. The backend always responds with a generic
 * success message regardless of whether the address exists, so this page
 * mirrors that and never reveals account existence.
 */
export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setValidationError(t('auth.forgotPassword.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError(t('auth.forgotPassword.emailInvalid'));
      return;
    }
    setValidationError(undefined);
    setErrorMessage(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      const res = await api.forgotPassword(email);
      setSuccessMessage(res.message);
    } catch (err: unknown) {
      setErrorMessage(
        getApiErrorMessage(err, t('auth.forgotPassword.sendFailed')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <LanguageSwitch className="justify-end -mt-2 -mr-2 mb-2" />
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🐾</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.forgotPassword.heading')}</h1>
          <p className="text-sm text-gray-600 mt-2">
            {t('auth.forgotPassword.intro')}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-sage-50 border border-sage-200 rounded-lg">
            <p className="text-sm text-sage-800 font-medium">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label={t('auth.forgotPassword.emailLabel')}
            name="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (validationError) setValidationError(undefined);
            }}
            error={validationError}
            placeholder={t('auth.forgotPassword.emailPlaceholder')}
            required
            autoComplete="email"
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={submitting}
            disabled={submitting}
          >
            {t('auth.forgotPassword.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-sage-600 font-medium hover:text-sage-700 transition-colors"
          >
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </div>
      </Card>
    </div>
  );
};
